import { Link } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHeading, FinalCTA, BookButton } from "@/components/shared";
import { images, treatments, routes } from "@/lib/site";
import { combine, localBusinessSchema, breadcrumbSchema, faqSchema } from "@/lib/schema";

const helpAreas = [
  "Back discomfort and lower back strain",
  "Neck discomfort and stiffness",
  "Posture-related concerns",
  "Mobility and movement limitations",
  "Spine-related discomfort",
  "Joint stiffness and everyday aches",
];

const visitSteps = [
  { n: "1", t: "Consultation", d: "We listen to your concern, symptoms and history." },
  { n: "2", t: "Assessment", d: "A careful physical assessment to understand the cause." },
  { n: "3", t: "Personalized Approach", d: "A care approach matched to your specific needs." },
  { n: "4", t: "Treatment Plan", d: "Clear, appropriate care with your comfort in mind." },
  { n: "5", t: "Follow-up", d: "Ongoing review and monitoring of your progress." },
];

const treatmentFaqs = [
  { q: "Is chiropractic treatment painful?", a: "Chiropractic care is generally gentle and focused on your comfort. Dr. Satyam explains each step and adapts the approach based on your response and concern." },
  { q: "How many sessions will I need?", a: "This varies from person to person and depends on your concern. After your assessment, Dr. Satyam will discuss a realistic, personalized plan with you." },
  { q: "Do I need a referral to visit?", a: "No referral is required. You can book a consultation directly by phone, WhatsApp or the appointment form." },
  { q: "Is chiropractic care an alternative to surgery?", a: "Chiropractic care focuses on conservative, non-surgical approaches. Where a concern needs other care, Dr. Satyam will advise and refer appropriately." },
];

export default function Treatments() {
  const jsonLd = combine(
    localBusinessSchema,
    faqSchema(treatmentFaqs),
    breadcrumbSchema([
      { name: "Home", path: routes.home },
      { name: "Chiropractic Treatment", path: routes.treatments },
    ])
  );

  return (
    <>
      <SEO
        path={routes.treatments}
        title="Chiropractic Treatment in Varanasi | Dr. Satyam Jaiswal"
        description="Chiropractic treatment in Varanasi with Dr. Satyam Jaiswal. Non-surgical, personalized care for back pain, neck pain, posture and spine concerns. Book your consultation."
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Chiropractic Treatments"
        title="Chiropractic Treatment in Varanasi"
        sub="Personalized, non-surgical care approaches designed to support better movement, comfort and long-term function."
        breadcrumb={[{ label: "Treatments" }]}
      />

      {/* What is chiropractic */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">What is Chiropractic Care?</span>
            <h2 className="font-heading font-extrabold tracking-tight text-navy text-3xl sm:text-4xl leading-[1.1]">
              Hands-On Care for Your Spine, Joints &amp; Movement
            </h2>
            <p className="mt-5 text-ink leading-relaxed">
              Chiropractic care is a non-surgical approach that focuses on the health of your spine,
              joints and muscles. Using careful, hands-on techniques, the aim is to support better
              movement, ease discomfort and help your body function more comfortably.
            </p>
            <p className="mt-4 text-ink leading-relaxed">
              At the heart of the approach is understanding <em>your</em> concern — so care is
              personalized, responsible and focused on long-term improvement rather than only
              temporary relief.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <img src={images.adjustment} alt="Chiropractic treatment session in Varanasi" loading="lazy" className="rounded-2xl w-full object-cover shadow-md aspect-[4/3]" />
          </Reveal>
        </div>
      </section>

      {/* How it may help */}
      <section className="bg-soft py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="How Chiropractic Care May Help"
            title="Support for Everyday Pain &amp; Mobility Concerns"
            sub="Chiropractic care may help with a range of musculoskeletal concerns. Care is always based on a careful, individual assessment."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            {helpAreas.map((h, i) => (
              <Reveal key={h} delay={i * 50}>
                <div className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 shadow-sm">
                  <CheckCircle2 className="h-5 w-5 text-brand-orange shrink-0" />
                  <span className="text-sm font-medium text-navy">{h}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments & approaches */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Treatments &amp; Care Approaches" title="Our Care Approaches" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatments.map((t, i) => (
              <Reveal key={t.name} delay={i * 60}>
                <div className="h-full bg-soft rounded-2xl p-8 hover:shadow-md transition-shadow duration-300">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-orange/10">
                    <t.icon className="h-6 w-6 text-brand-orange" />
                  </div>
                  <h3 className="mt-5 font-heading font-bold text-navy text-xl">{t.name}</h3>
                  <p className="mt-2 text-ink leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="relative overflow-hidden bg-navy py-20 lg:py-28">
        <div className="absolute inset-0 dotted-grid opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading light center eyebrow="What to Expect" title="During Your Visit" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {visitSteps.map((s, i) => (
              <Reveal key={s.n} delay={i * 70}>
                <div className="h-full rounded-2xl bg-white/5 border border-white/10 p-6">
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-orange font-heading font-bold text-white">{s.n}</span>
                  <h3 className="mt-4 font-heading font-bold text-white">{s.t}</h3>
                  <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={routes.symptoms} className="inline-flex items-center gap-2 font-bold text-white hover:text-brand-orange transition-colors group">
              Explore Symptoms <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to={routes.about} className="inline-flex items-center gap-2 font-bold text-white hover:text-brand-orange transition-colors group">
              About Dr. Satyam <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-soft py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="FAQs" title="Treatment Questions" />
          <Accordion type="single" collapsible className="w-full">
            {treatmentFaqs.map((f, i) => (
              <AccordionItem key={i} value={`tf-${i}`} className="bg-white rounded-xl px-5 mb-3 border-none shadow-sm">
                <AccordionTrigger data-testid={`treatment-faq-${i}`} className="text-left font-heading font-semibold text-navy hover:no-underline hover:text-brand-orange py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-ink leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <BookButton label="Book Your Consultation" testId="treatments-book-btn" />
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
